import { useCallback, useMemo, useRef } from "react";
import { Controller } from "react-hook-form";
import { createColumnHelper, useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import { Select, TextInput, TextInputWithPrefix, SegmentedToggle, Button } from "../../ui";
import { withDerivedTierBoundaries } from "../TierTable";
import { CYCLE_TO_DAYS } from "../../../_utils/pricing";
import "./_pricing-model-editor.scss";

const CYCLE_OPTIONS = Object.keys(CYCLE_TO_DAYS).map((cycle) => ({ label: cycle, value: cycle }));
const PRICING_MODEL_OPTIONS = ["Flat Fee", "Per Unit", "Volume", "Tiered", "Percent"].map((v) => ({ label: v, value: v }));

const PRICE_MODELS = ["Flat Fee", "Per Unit", "Percent"];
const UNIT_NAME_MODELS = ["Per Unit", "Volume", "Tiered"];
const TIERED_MODELS = ["Volume", "Tiered"];

const columnHelper = createColumnHelper();

/**
 * Authors a plan or product's own pricing model (feeType/terms/
 * collectionType/billingCycle/pricingCycle/pricingModel + the
 * price-or-pricingTiers/unitName fields that follow from pricingModel) -
 * the inverse of every other _components/billing editor, which all CONSUME
 * an already-defined plan/product. Shared by Plans' and Products' Pricing
 * Configuration tabs - both entities author the identical field set.
 *
 * Replaces the old-package FormInputTieredPricing for the Volume/Tiered
 * tier-authoring table. Reuses withDerivedTierBoundaries (the one
 * genuinely shared piece) to keep each tier's `to` boundary derived from
 * the next tier's `from`, same rule TierTable already enforces for
 * consumption-time tier editing.
 */
const PricingModelEditor = ({ control, watch, setValue, trigger, currencySign = "" }) => {
  const pricingModel = watch("pricingModel");
  const pricingTiers = watch("pricingTiers") || [];

  const showPrice = PRICE_MODELS.includes(pricingModel);
  const showUnitName = UNIT_NAME_MODELS.includes(pricingModel);
  const showTiers = TIERED_MODELS.includes(pricingModel);

  const handlePricingModelChange = (value, onChange) => {
    onChange(value);
    trigger?.("pricingModel");
    if (TIERED_MODELS.includes(value)) {
      setValue("price", "");
      if (!pricingTiers.length) {
        setValue("pricingTiers", withDerivedTierBoundaries([{ from: "0", to: "", unitPrice: "" }]));
      }
    } else if (PRICE_MODELS.includes(value)) {
      setValue("pricingTiers", []);
    }
  };

  // handleTierFieldChange/handleAddTier/handleRemoveTier read pricingTiers
  // through this ref (reassigned every render, no effect needed) rather
  // than closing over it directly, so the handlers themselves can be
  // stable useCallbacks - see tierColumns below for why that stability
  // matters.
  const pricingTiersRef = useRef(pricingTiers);
  pricingTiersRef.current = pricingTiers;

  const handleTierFieldChange = useCallback(
    (index, field, value) => {
      const next = pricingTiersRef.current.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier));
      setValue("pricingTiers", field === "from" ? withDerivedTierBoundaries(next) : next);
    },
    [setValue]
  );

  const handleAddTier = useCallback(() => {
    const current = pricingTiersRef.current;
    const lastTo = current[current.length - 1]?.to;
    const nextFrom = lastTo === "" || lastTo === undefined ? "" : String((parseFloat(lastTo) || 0) + 1);
    setValue("pricingTiers", withDerivedTierBoundaries([...current, { from: nextFrom, to: "", unitPrice: "" }]));
  }, [setValue]);

  const handleRemoveTier = useCallback(
    (index) => {
      setValue("pricingTiers", withDerivedTierBoundaries(pricingTiersRef.current.filter((_, i) => i !== index)));
    },
    [setValue]
  );

  // Deliberately NOT dependent on `pricingTiers` itself (only the now-stable
  // handlers above and currencySign) - TanStack's flexRender treats a
  // changed cell-render-function identity as a new component type and
  // remounts it, which was silently stealing focus from these inputs after
  // every keystroke when this memo depended on pricingTiers directly (it
  // changes on every edit). See _components/billing/TierTable's identical
  // fix for the full explanation.
  const tierColumns = useMemo(
    () => [
      columnHelper.accessor("from", {
        header: "From",
        cell: (info) => (
          <TextInput compact value={info.getValue()} onChange={(value) => handleTierFieldChange(info.row.index, "from", value)} />
        ),
      }),
      columnHelper.accessor("to", {
        header: "To",
        cell: (info) => <TextInput compact value={info.getValue() === "" ? "∞" : info.getValue()} disabled />,
      }),
      columnHelper.accessor("unitPrice", {
        header: "Unit price",
        cell: (info) => (
          <TextInputWithPrefix
            compact
            prefix={currencySign}
            value={info.getValue()}
            onChange={(value) => handleTierFieldChange(info.row.index, "unitPrice", value)}
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: () => null,
        cell: (info) =>
          info.row.index > 0 && (
            <Button
              variant="ghost-danger"
              size="md"
              iconOnly
              icon={<Trash2 size={16} />}
              onClick={() => handleRemoveTier(info.row.index)}
              aria-label={`Remove tier ${info.row.index + 1}`}
            />
          ),
      }),
    ],
    [currencySign, handleTierFieldChange, handleRemoveTier]
  );

  const tierTable = useReactTable({
    data: pricingTiers,
    columns: tierColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_row, index) => index,
  });

  return (
    <div className="pricing-model-editor">
      <Controller
        name="feeType"
        control={control}
        render={({ field }) => (
          <SegmentedToggle id="feeType" label="Fee Type" options={["Fixed", "Usage based"]} value={field.value} onChange={field.onChange} />
        )}
      />

      <Controller
        name="terms"
        control={control}
        render={({ field }) => (
          <SegmentedToggle id="terms" label="Terms" options={["Pay in advance", "Postpaid"]} value={field.value} onChange={field.onChange} />
        )}
      />

      <Controller
        name="collectionType"
        control={control}
        render={({ field }) => (
          <SegmentedToggle
            id="collectionType"
            label="Collection"
            options={["One-time", "Recurring"]}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        name="billingCycle"
        control={control}
        render={({ field }) => (
          <Select id="billingCycle" label="Billing cycle (charged monthly, quarterly, or yearly)" options={CYCLE_OPTIONS} value={field.value} onChange={field.onChange} />
        )}
      />

      <Controller
        name="pricingCycle"
        control={control}
        render={({ field }) => (
          <Select id="pricingCycle" label="Pricing cycle (price per month/quarter/year)" options={CYCLE_OPTIONS} value={field.value} onChange={field.onChange} />
        )}
      />

      <Controller
        name="pricingModel"
        control={control}
        render={({ field }) => (
          <Select
            id="pricingModel"
            label="Pricing model (per seat, tiered, volume based)"
            options={PRICING_MODEL_OPTIONS}
            value={field.value}
            onChange={(value) => handlePricingModelChange(value, field.onChange)}
          />
        )}
      />

      {showPrice && (
        <Controller
          name="price"
          control={control}
          render={({ field }) => (
            <TextInputWithPrefix
              id="price"
              label="Enter price"
              prefix={currencySign}
              value={field.value}
              onChange={field.onChange}
              tooltip="This field is only enabled when pricing model is Flat Fee, Per Unit, or Percent"
            />
          )}
        />
      )}

      {showTiers && (
        <div className="pricing-model-editor__tiers">
          <span className="pricing-model-editor__tiers-label">Tiered Pricing</span>
          <div className="pricing-model-editor__tier-scroll">
            <table className="pricing-model-editor__tier-table">
              <thead>
                {tierTable.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {tierTable.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>
                    <Button variant="ghost" size="md" onClick={handleAddTier}>
                      + Add tier
                    </Button>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {showUnitName && (
        <Controller
          name="unitName"
          control={control}
          render={({ field }) => (
            <TextInput
              id="unitName"
              label="Unit name (per seat, user, employee)"
              value={field.value}
              onChange={field.onChange}
              tooltip="This field is only enabled when pricing model is Volume, Tiered, or Per Unit"
            />
          )}
        />
      )}
    </div>
  );
};

export default PricingModelEditor;
